import { Ng2StateDeclaration, StatesModule } from '@uirouter/angular';
import { FiStateNames } from '../../shared/fi-state-proxy/fi-state-names';
import { PlandayService } from './planday.service';
import { PlandaySettingsComponent } from './planday-settings/planday-settings.component';
import { PlandayEditContainerComponent } from './planday-edit-container/planday-edit-container.component';
import { StateAccessCheckService } from '../../shared/fi-shared-services/state-access-check.service';
import { AccessRightType, ApplicationSettingsType } from '../../shared/types';
import { AreaService } from '@fi-app/area/area.service';
import { PlandayRolesMatchingComponent } from '@fi-app/external-platform-integration/planday/planday-roles-matching/planday-roles-matching.component';
import { RoleService } from '@fi-app/role/services/role.service';
import { PlandayUnitsMatchingComponent } from '@fi-app/external-platform-integration/planday/planday-units-matching/planday-units-matching.component';
import { BusinessUnitService } from '@fi-app/business-unit/business-unit.service';
import { PlandayUsersMatchingComponent } from '@fi-app/external-platform-integration/planday/planday-users-matching/planday-users-matching.component';
import { BusinessAreaService } from '@fi-app/business-area/business-area.service';
import { PlandayMatchUnitsWithAreasComponent } from '@fi-app/external-platform-integration/planday/planday-match-units-with-areas/planday-match-units-with-areas.component';
import { IPlandayUnitMatch } from '@fi-app/external-platform-integration/planday/planday';
import { PlandayImportUnitsComponent } from '@fi-app/external-platform-integration/planday/planday-import-units/planday-import-units.component';
import { PlandayImportUsersComponent } from '@fi-app/external-platform-integration/planday/planday-import-users/planday-import-users.component';
import { PlandayImportLogComponent } from '@fi-app/external-platform-integration/planday/planday-import-log/planday-import-log.component';

const PlandayState: Ng2StateDeclaration = {
    name: FiStateNames.OtherSettings.Planday._name,
    url: '/Planday',
    abstract: true,
    views: {
        '@': {
            component: PlandayEditContainerComponent,
        },
    },
};

const PlandayEditSettingsState: Ng2StateDeclaration = {
    name: FiStateNames.OtherSettings.Planday.Settings,
    url: '/Settings',
    component: PlandaySettingsComponent,
    resolve: {
        plandaySettings: [
            PlandayService,
            (plandayService: PlandayService) => {
                return plandayService.getPlandaySettings();
            },
        ],
        areas: [
            AreaService,
            (areService: AreaService) => {
                return areService.getLightWeightFlattenAreas();
            },
        ],
        businessAreas: [
            BusinessAreaService,
            (businessAreaService: BusinessAreaService) => {
                return businessAreaService.getLightWeightBusinessAreas();
            },
        ],
    },
};

const PlandayRolesMatchingState: Ng2StateDeclaration = {
    name: FiStateNames.OtherSettings.Planday.RolesMatching,
    url: '/MatchRoles',
    component: PlandayRolesMatchingComponent,
    resolve: {
        plandayRoleMatches: [
            PlandayService,
            (plandayService: PlandayService) => {
                return plandayService.getExistingRoleMatches();
            },
        ],
        roles: [
            RoleService,
            (roleService: RoleService) => {
                return roleService.getLightWeightRoles();
            },
        ],
    },
};

const PlandayImportUnitsState: Ng2StateDeclaration = {
    name: FiStateNames.OtherSettings.Planday.ImportUnits,
    url: '/ImportUnits',
    component: PlandayImportUnitsComponent,
    resolve: {},
};

const PlandayUnitsMatchingState: Ng2StateDeclaration = {
    name: FiStateNames.OtherSettings.Planday.UnitsMatching,
    url: '/MatchUnits',
    component: PlandayUnitsMatchingComponent,
    resolve: {
        plandayUnitsMatchData: [
            PlandayService,
            (plandayService: PlandayService) => {
                return plandayService.getPlandayUnitsMatchData();
            },
        ],
    },
};

const MatchUnitsWithAreasState: Ng2StateDeclaration = {
    name: FiStateNames.OtherSettings.Planday.MatchUnitsWithAreas,
    url: '/MatchUnitsWithAreas',
    component: PlandayMatchUnitsWithAreasComponent,
    resolve: {
        plandayUnitMatches: [
            PlandayService,
            (plandayService: PlandayService) => {
                return new Promise<IPlandayUnitMatch[]>((resolve, reject) => {
                    plandayService
                        .getPlandayUnitsMatchData(true)
                        .then((matchData) => resolve(matchData.PlandayUnitMatches), reject);
                });
            },
        ],
        areas: [
            AreaService,
            (areService: AreaService) => {
                return areService.getLightWeightFlattenAreas();
            },
        ],
        businessAreas: [
            BusinessAreaService,
            (businessAreaService: BusinessAreaService) => {
                return businessAreaService.getLightWeightBusinessAreas();
            },
        ],
    },
};

const PlandayImportUsersState: Ng2StateDeclaration = {
    name: FiStateNames.OtherSettings.Planday.ImportUsers,
    url: '/ImportUsers',
    component: PlandayImportUsersComponent,
    resolve: {},
};

const PlandayUsersMatchingState: Ng2StateDeclaration = {
    name: FiStateNames.OtherSettings.Planday.UsersMatching,
    url: '/MatchUsers',
    component: PlandayUsersMatchingComponent,
    resolve: {
        plandayUsersMatchData: [
            PlandayService,
            (plandayService: PlandayService) => {
                return plandayService.getPlandayUsersMatchData();
            },
        ],
    },
};

const PlandayImportLogState: Ng2StateDeclaration = {
    name: FiStateNames.OtherSettings.Planday.ImportLog,
    url: '/ImportLog?:filterType&:sortColumn&:sortDirection&:offset',
    params: {
        //selectedTabIndex: undefined,
        //selectedSyncLogId: undefined,
    },
    reloadOnSearch: false,
    component: PlandayImportLogComponent,
    resolve: {
        /*selectedTabIndex: [FiStateService, Transition, (fiStateService: FiStateService, tr: Transition) => {
            return fiStateService.getParams(tr).selectedTabIndex;
        }],
        selectedSyncLogId: [FiStateService, Transition, (fiStateService: FiStateService, tr: Transition) => {
            return fiStateService.getParams(tr).selectedSyncLogId;
        }]*/
    },
};

export const PlandayStates: StatesModule = {
    states: [
        PlandayState,
        PlandayEditSettingsState,
        PlandayRolesMatchingState,
        PlandayUnitsMatchingState,
        MatchUnitsWithAreasState,
        PlandayImportUsersState,
        PlandayUsersMatchingState,
        PlandayImportUnitsState,
        PlandayImportLogState,
    ],
};

export function registerPlandayStateRules(stateAccessCheckService: StateAccessCheckService) {
    stateAccessCheckService.registerStateRules([FiStateNames.OtherSettings.Planday._name], {
        SettingType: [ApplicationSettingsType.Planday],
        AccessType: AccessRightType.Admin,
    });
}
